// Cockpit page state (spec §4.2.1): kept in a store, never inside presentation
// components. `tab`, `view`, `inspector` and `id` live in the URL instead. Run
// status transitions go through the pure `runReducer` (§9.5); the store only
// adds I/O, in-flight guards and announcements.
import { create } from 'zustand';
import { announce, cancelAnnouncements } from '../../state/announcer.ts';
import { api, ApiError } from '../onboarding/api.ts';
import {
  EVENTS_PAGE_SIZE,
  finishedSentence,
  initialRunState,
  runReducer,
  stepCounts,
  type RunState,
} from './model.ts';
import type { EngagementView, MemoryEntry, RunEvent, RunEventsPage, RunView } from './types.ts';

export interface CockpitError {
  message: string;
  reason?: string;
}

interface CockpitState {
  engagement: EngagementView | null;
  run: RunView | null;
  runState: RunState;
  events: RunEvent[];
  paused: boolean;
  /** Snapshot taken at Pause: rows and total stay as they were until Resume. */
  frozen: { events: RunEvent[]; total: number } | null;
  expanded: boolean;
  loading: boolean;
  error: CockpitError | null;
  /** A start request is in flight (repeated presses create one run). */
  startPending: boolean;
  /** Engagement memory; null when the backend serves none for this engagement. */
  memory: MemoryEntry[] | null;
  memoryError: string | null;
  load: (id: string) => Promise<void>;
  /** Saves a note to memory; resolves false when it was not saved. */
  remember: (id: string, text: string) => Promise<boolean>;
  loadEarlier: (id: string) => Promise<void>;
  togglePause: () => void;
  toggleExpanded: () => void;
  startRun: (id: string) => Promise<void>;
  stopRun: (id: string) => Promise<void>;
  /** Applies a backend run view and the events that came with it. */
  applyRunView: (view: RunView, page: RunEventsPage | null) => void;
  /** A poll failed: keep the last confirmed view and mark it stale. */
  pollFailed: () => void;
  reset: () => void;
}

const bySequence = (a: RunEvent, b: RunEvent) => a.sequence - b.sequence;

export function mergeEvents(current: RunEvent[], incoming: readonly RunEvent[]): RunEvent[] {
  const seen = new Set(current.map((e) => e.id));
  return [...current, ...incoming.filter((e) => !seen.has(e.id))].sort(bySequence);
}

const OFFLINE: CockpitError = {
  message: "We can't reach Traceira right now. The last confirmed view is shown below.",
};

const toError = (e: unknown): CockpitError =>
  e instanceof ApiError ? { message: e.message } : OFFLINE;

// Run state changes announce immediately; a finished step goes through the
// throttled channel (one per 8 s, latest wins) and never while presentation is
// paused. Raw terminal lines are never announced (§12).
function announceChanges(
  before: RunState,
  after: RunState,
  prev: RunView | null,
  view: RunView,
  paused: boolean,
) {
  const counts = stepCounts(view.steps);
  if (before.status !== after.status) {
    const text = finishedSentence(after.status, counts);
    if (text) announce(text);
    else if (after.status === 'failed') announce(`Run failed. ${counts.sentence}.`);
  }
  const was = prev && prev.attempt === view.attempt ? stepCounts(prev.steps).finished : 0;
  if (counts.finished > was && !paused) {
    announce(`Step finished. ${counts.sentence}.`, { channel: 'engagement-run', throttleMs: 8000 });
  }
}

export const useCockpit = create<CockpitState>()((set, get) => ({
  engagement: null,
  run: null,
  runState: initialRunState(),
  events: [],
  paused: false,
  frozen: null,
  expanded: false,
  loading: true,
  error: null,
  startPending: false,
  memory: null,
  memoryError: null,

  load: async (id) => {
    set({ loading: true, error: null });
    try {
      const engagement = await api.engagement(id);
      let run: RunView | null = null;
      let events: RunEvent[] = [];
      try {
        run = await api.engagementRun(id);
        const page = await api.engagementRunEvents(id, { limit: EVENTS_PAGE_SIZE });
        // Defensive: the contract promises events[]; anything else renders as
        // an empty history, never a crash (§4.9 honest states).
        events = Array.isArray(page.events) ? page.events : [];
      } catch (e) {
        // A 404 on the run: "Running checks isn't available yet" (§4.9).
        if (!(e instanceof ApiError && e.status === 404)) throw e;
      }
      let memory: MemoryEntry[] | null = null;
      let memoryError: string | null = null;
      try {
        const page = await api.engagementMemory(id);
        memory = Array.isArray(page.entries) ? page.entries : [];
      } catch (e) {
        // A 404: memory isn't served yet. Anything else is a failed read, said as such.
        if (!(e instanceof ApiError && e.status === 404)) memoryError = toError(e).message;
      }
      set({
        engagement,
        run,
        events,
        runState: run ? initialRunState(run) : initialRunState(),
        memory,
        memoryError,
        loading: false,
      });
    } catch (e) {
      set({
        loading: false,
        error:
          e instanceof ApiError
            ? { message: e.message, reason: String(e.body.reason ?? '') || undefined }
            : OFFLINE,
      });
    }
  },

  remember: async (id, text) => {
    try {
      const entry = await api.addEngagementMemory(id, text);
      if (get().engagement?.id !== id) return true;
      set((s) => ({ memory: [...(s.memory ?? []), entry], memoryError: null }));
      announce('Saved to memory.');
      return true;
    } catch (e) {
      const message =
        e instanceof ApiError && e.status === 404
          ? 'Engagement memory isn’t available from the backend yet. Nothing was saved.'
          : toError(e).message;
      set({ memoryError: message });
      announce(message);
      return false;
    }
  },

  loadEarlier: async (id) => {
    const oldest = get().events[0]?.sequence;
    if (oldest === undefined) return;
    try {
      // The previous page (limit = 200 per §4.5): the contract only has a forward
      // cursor, so ask for the page that ends just before `oldest`.
      const page = await api.engagementRunEvents(id, {
        after: Math.max(0, oldest - 1 - EVENTS_PAGE_SIZE),
        limit: EVENTS_PAGE_SIZE,
      });
      set((s) => ({
        events: mergeEvents(
          s.events,
          page.events.filter((e) => e.sequence < oldest),
        ),
      }));
    } catch {
      // Keep the current rows; the truncated row stays visible.
    }
  },

  togglePause: () => {
    if (get().paused) {
      set({ paused: false, frozen: null });
      return;
    }
    // Pause also pauses the throttled announcement channel (§12).
    cancelAnnouncements('engagement-run');
    const { events, run } = get();
    set({ paused: true, frozen: { events, total: run?.eventCount ?? events.length } });
  },

  toggleExpanded: () => set((s) => ({ expanded: !s.expanded })),

  applyRunView: (view, page) => {
    const { engagement, run: prev, runState: before, paused } = get();
    // A late response after leaving the cockpit must not repopulate it.
    if (!engagement || engagement.id !== view.engagementId) return;
    const after = runReducer(before, { type: 'run-view', view });
    set((s) => ({
      run: view,
      runState: after,
      events: page ? mergeEvents(s.events, page.events) : s.events,
      error: null,
    }));
    announceChanges(before, after, prev, view, paused);
  },

  pollFailed: () => set((s) => ({ runState: runReducer(s.runState, { type: 'stale' }) })),

  startRun: async (id) => {
    const { runState, startPending } = get();
    // Repeated presses create one run (§9.5): in flight, or already active.
    if (startPending || runState.status === 'running' || runState.status === 'stop-requested') {
      return;
    }
    set({ startPending: true });
    try {
      const view = await api.startEngagementRun(id);
      get().applyRunView(view, null);
      announce('Run started.');
    } catch (e) {
      set({ error: toError(e) });
    } finally {
      set({ startPending: false });
    }
  },

  stopRun: async (id) => {
    // Repeats are no-ops: only a running run takes a stop request, and it is
    // recorded before the request goes out.
    if (get().runState.status !== 'running') return;
    set((s) => ({
      runState: runReducer(s.runState, {
        type: 'stop-requested',
        at: new Date().toISOString(),
        by: 'You',
      }),
    }));
    announce('Stop requested.');
    try {
      const view = await api.stopEngagementRun(id);
      // Only a backend answer other than "running" replaces the request (§9.4).
      get().applyRunView(view, null);
    } catch (e) {
      // The request itself failed, so nothing was requested: Stop is available
      // again. (A merely delayed acknowledgement keeps "Stop requested".)
      set((s) => ({
        runState: runReducer(s.runState, { type: 'stop-failed' }),
        error: toError(e),
      }));
      announce('Stop could not be requested. Try again.');
    }
  },

  // Start over (the shell's button) clears local view state only: the run is not
  // stopped and backend records stay (§9.5).
  reset: () =>
    set({
      engagement: null,
      run: null,
      runState: initialRunState(),
      events: [],
      paused: false,
      frozen: null,
      expanded: false,
      loading: false,
      error: null,
      startPending: false,
      memory: null,
      memoryError: null,
    }),
}));
